import React from 'react'
import { ActionIcon, Group, Stack, Text } from '@mantine/core'
import { PlusIcon, TrashIcon } from '@radix-ui/react-icons'

import { TimePickerInput } from './TimePickerInput'
import { DayRanges, WEEKDAY_LABELS } from './weekUtils'

export const DayAvailabilityRow: React.FC<{
  weekday: number
  ranges: DayRanges
  errors?: Record<number, string>
  readOnly: boolean
  onChange: (ranges: DayRanges) => void
}> = ({ weekday, ranges, errors, readOnly, onChange }) => {
  const updateRange = (index: number, update: Partial<DayRanges[number]>) => {
    onChange(
      ranges.map((range, i) => (i === index ? { ...range, ...update } : range)),
    )
  }

  const addRange = () => onChange([...ranges, { start: '', end: '' }])

  const removeRange = (index: number) =>
    onChange(ranges.filter((_, i) => i !== index))

  return (
    <Group align="flex-start" noWrap className="w-full">
      <Text size="sm" className="w-24 pt-2 shrink-0">
        {WEEKDAY_LABELS[weekday]}
      </Text>
      <Stack className="flex-1" spacing={4}>
        {ranges.map((range, index) => (
          <Stack key={index} spacing={2}>
            <Group noWrap spacing="xs">
              <TimePickerInput
                ariaLabel={`${WEEKDAY_LABELS[weekday]} start time`}
                placeholder="Start time"
                value={range.start}
                disabled={readOnly}
                onChange={value => updateRange(index, { start: value })}
                className="flex-1"
              />
              <Text size="sm" color="dimmed">
                →
              </Text>
              <TimePickerInput
                ariaLabel={`${WEEKDAY_LABELS[weekday]} end time`}
                placeholder="End time"
                value={range.end}
                disabled={readOnly}
                onChange={value => updateRange(index, { end: value })}
                className="flex-1"
              />
              {!readOnly && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={
                    index === 0
                      ? `Add another time range on ${WEEKDAY_LABELS[weekday]}`
                      : `Remove this ${WEEKDAY_LABELS[weekday]} time range`
                  }
                  onClick={() =>
                    index === 0 ? addRange() : removeRange(index)
                  }
                >
                  {index === 0 ? <PlusIcon /> : <TrashIcon />}
                </ActionIcon>
              )}
            </Group>
            {errors?.[index] && (
              <Text size="xs" color="red">
                {errors[index]}
              </Text>
            )}
          </Stack>
        ))}
      </Stack>
    </Group>
  )
}
